import React, { useState } from "react";
import {
  maintenanceApi,
  StationDropdownItem,
  OrgItem,
  PricingOverrideItem,
  ServiceTypeWithTaxes,
} from "../../../api/maintenance";
import { X } from "lucide-react";
import { useNotice } from "../shared/useNotice";

interface Props {
  stations: StationDropdownItem[];
  orgs: OrgItem[];
  serviceTypes: ServiceTypeWithTaxes[];
  editingOverride: PricingOverrideItem | null;
  defaultStationId?: number;
  defaultOrgId?: number;
  onClose: () => void;
  onDone: () => void;
}

const PricingOverrideForm: React.FC<Props> = ({
  stations, orgs, serviceTypes, editingOverride,
  defaultStationId, defaultOrgId, onClose, onDone,
}) => {
  const isEdit = !!editingOverride;

  const [orgId, setOrgId] = useState<number | "">(editingOverride?.org_id || defaultOrgId || "");
  const [stationId, setStationId] = useState<number | "">(editingOverride?.charging_station_id || defaultStationId || "");
  const [chargerType, setChargerType] = useState(editingOverride?.charger_type || "AC");
  const [pricePerKwh, setPricePerKwh] = useState(editingOverride?.price_per_kwh?.toString() || "");
  const [isDefault, setIsDefault] = useState(editingOverride?.is_default ?? true);
  const [priority, setPriority] = useState(editingOverride?.priority?.toString() || "0");
  const [serviceTypeKey, setServiceTypeKey] = useState(editingOverride?.service_type_key || "charging_inclusive");

  // For time-bounded: use ISO string format for datetime-local inputs
  const toLocalISO = (d: string | null) => {
    if (!d) return "";
    try {
      const date = new Date(d);
      return date.toISOString().slice(0, 16);
    } catch {
      return "";
    }
  };
  const [validFrom, setValidFrom] = useState(toLocalISO(editingOverride?.valid_from || null));
  const [validThru, setValidThru] = useState(toLocalISO(editingOverride?.valid_thru || null));

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useNotice("error", "");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId || !stationId || !pricePerKwh) {
      setError("Organization, Station, and Price are required");
      return;
    }
    if (!isDefault && (!validFrom || !validThru)) {
      setError("Time-bounded overrides require Valid From and Valid Thru");
      return;
    }

    setSubmitting(true);
    setError("");

    // For default overrides, set a wide time window
    const from = isDefault ? "2000-01-01T00:00:00" : new Date(validFrom).toISOString();
    const thru = isDefault ? "2099-12-31T23:59:59" : new Date(validThru).toISOString();

    // Overrides are immutable — only CREATE. To change a price you disable the old
    // override (on the pricing page) and create a new one here.
    try {
      await maintenanceApi.createPricingOverride({
        org_id: orgId as number,
        charging_station_id: stationId as number,
        charger_type: chargerType,
        price_per_kwh: parseFloat(pricePerKwh),
        valid_from: from,
        valid_thru: thru,
        is_default: isDefault,
        priority: parseInt(priority) || 0,
        service_type_key: serviceTypeKey,
      });
      onDone();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to save override");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedServiceType = serviceTypes.find((st) => st.service_type_key === serviceTypeKey);

  return (
    <div className="maint-form-overlay">
      <div className="maint-form-card maint-form-card--wide">
        <div className="maint-form-header">
          <h3>{isEdit ? "Edit" : "Add"} Pricing Override</h3>
          <button onClick={onClose} className="maint-btn-icon"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="maint-form-row">
            <label>
              Organization *
              <select value={orgId} onChange={(e) => setOrgId(Number(e.target.value))} required disabled={isEdit}>
                <option value="">Select org...</option>
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </label>
            <label>
              Station *
              <select value={stationId} onChange={(e) => setStationId(Number(e.target.value))} required disabled={isEdit}>
                <option value="">Select station...</option>
                {stations.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="maint-form-row">
            <label>
              Charger Type
              <select value={chargerType} onChange={(e) => setChargerType(e.target.value)}>
                <option value="AC">AC</option>
                <option value="DC">DC</option>
              </select>
            </label>
            <label>
              Price per kWh (&#8377;) *
              <input type="number" step="0.01" min="0" value={pricePerKwh} onChange={(e) => setPricePerKwh(e.target.value)} required />
            </label>
          </div>

          <div className="maint-form-row">
            <label>
              Service Type
              <select value={serviceTypeKey} onChange={(e) => setServiceTypeKey(e.target.value)}>
                {serviceTypes.map((st) => (
                  <option key={st.service_type_key} value={st.service_type_key}>
                    {st.service_type_key} ({st.tax_model})
                  </option>
                ))}
              </select>
            </label>
            <label>
              Override Type
              {!isEdit ? (
                <select value={isDefault ? "default" : "time"} onChange={(e) => setIsDefault(e.target.value === "default")}>
                  <option value="default">Default (always active)</option>
                  <option value="time">Time-bounded</option>
                </select>
              ) : (
                <input type="text" value={isDefault ? "Default" : "Time-bounded"} disabled />
              )}
            </label>
          </div>

          {/* Tax summary */}
          {selectedServiceType && selectedServiceType.tax_components.length > 0 && (
            <div style={{ background: "#f5f5ff", padding: "8px 12px", borderRadius: "6px", marginBottom: "8px", fontSize: "13px" }}>
              <strong>Tax ({selectedServiceType.tax_model}):</strong>{" "}
              {selectedServiceType.tax_components.map((tc) => `${tc.display_name} ${tc.tax_percentage}%`).join(" + ")}
            </div>
          )}

          {!isDefault && (
            <div className="maint-form-row">
              <label>
                Valid From *
                <input type="datetime-local" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} required />
              </label>
              <label>
                Valid Thru *
                <input type="datetime-local" value={validThru} onChange={(e) => setValidThru(e.target.value)} required />
              </label>
            </div>
          )}

          {!isDefault && (
            <label>
              Priority (higher wins on overlap)
              <input type="number" value={priority} onChange={(e) => setPriority(e.target.value)} />
            </label>
          )}

          {error && <p className="maint-form-error">{error}</p>}
          <div className="maint-form-actions">
            <button type="button" onClick={onClose} className="maint-btn-secondary">Cancel</button>
            <button type="submit" disabled={submitting} className="maint-btn-primary">
              {submitting ? "Saving..." : isEdit ? "Update" : "Create"} Override
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PricingOverrideForm;
